import React, { ReactElement } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

import { Card, CardTitle, CardSubtitle, GlobalStyle } from './styles';
import { ContentType } from 'recharts/types/component/DefaultLegendContent';

interface TaskData {
	id: string;
	value: number;
}

interface TileProps {
	title: string;
	subtitle: string;
	allTasks: Array<TaskData>;
	milestoneTasks: Array<TaskData>;
}

interface GraphicData {
	x: number;
	y: number;
	width: number;
	height: number;
	value: number;
}

const TasksAndMilestone = (props: TileProps): ReactElement => {
	const STATUS_KEYS = ['Not Applicable', 'Complete', 'Past Due', 'In Progress', 'Not Due'];

	const calculateTotal = (data: Array<TaskData>) => {
		return data.reduce((sum, item) => sum + item.value, 0);
	};

	const formatData = (data: Array<TaskData>, title: string) => {
		const total = calculateTotal(data);
		const transformedData = data.reduce(
			(acc, item) => {
				acc[item.id] = item.value;
				return acc;
			},
			{ name: `${title} (${total})` }
		);
		return transformedData;
	};

	const calculateMaxValue = (data: Array<TaskData>) => {
		return Math.max(...data.map((item) => item.value), 0);
	};

	const data = [formatData(props.allTasks, 'All Tasks'), formatData(props.milestoneTasks, 'Milestone Tasks')];

	const maxAllTasksValue = calculateMaxValue(props.allTasks);
	const maxMilestoneTasksValue = calculateMaxValue(props.milestoneTasks);

	const renderCustomLabel = (props: GraphicData) => {
		const { x, y, width, height, value } = props;
		if (value === 0) return null;
		const midX = x + width / 2;
		const midY = y + height / 2;
		return (
			<text x={midX} y={midY} fill='#fff' textAnchor='middle' dominantBaseline='middle' style={{ fontSize: '14px', fontWeight: 500 }}>
				{value}
			</text>
		);
	};

	const renderCustomYAxisLabel = (props: GraphicData): ContentType => {
		const { x, y, value } = props;
		return (
			<text x={x} y={y - 12} fill='#000' textAnchor='start' dominantBaseline='middle' style={{ fontSize: '14px', fontWeight: 500 }}>
				{value}
			</text>
		);
	};

	const getRadius = (tasks: Array<any>, key: string): [number, number, number, number] => {
		let firstBarIndex = -1;
		let lastBarIndex = -1;

		// Find the first and last elements greater than 0
		for (let i = 0; i < tasks.length; i++) {
			if (tasks[i].value > 0) {
				if (firstBarIndex === -1) {
					firstBarIndex = i;
				}
				lastBarIndex = i;
			}
		}

		if (firstBarIndex === lastBarIndex) {
			return [6, 6, 6, 6];
		}

		if (tasks[firstBarIndex].id === key) {
			return [6, 0, 0, 6];
		}

		if (tasks[lastBarIndex].id === key) {
			return [0, 6, 6, 0];
		}

		return [0, 0, 0, 0];
	};

	const getColor = (key: string) => {
		switch (key) {
			case 'Complete':
				return '#1EA97C';
			case 'Past Due':
				return '#FF5757';
			case 'Not Applicable':
				return '#ACE874';
			case 'Not Due':
				return '#CC8925';
			case 'In Progress':
				return '#DEE2E6';
			default:
				return '#000';
		}
	};

	return (
		<>
			<GlobalStyle />
			<Card>
				<CardTitle>{props.title}</CardTitle>
				<CardSubtitle>{props.subtitle}</CardSubtitle>
				<ResponsiveContainer width='100%' height={55} debounce={50}>
					<BarChart
						layout='vertical'
						data={[formatData(props.allTasks, 'All Tasks')]}
						margin={{
							top: 0,
							right: 0,
							left: 0,
							bottom: 10,
						}}>
						<XAxis hide axisLine={false} type='number' domain={[0, maxAllTasksValue]} />
						<YAxis type='category' dataKey='name' hide />
						<Tooltip />

						{STATUS_KEYS.map((key) => (
							<Bar key={key} dataKey={key} fill={getColor(key)} stackId='1' barSize={25} radius={getRadius(props.allTasks, key)}>
								{key == 'Complete' && <LabelList dataKey='name' position='top' content={renderCustomYAxisLabel} />}
								<LabelList dataKey={key} content={renderCustomLabel} />
							</Bar>
						))}
					</BarChart>
				</ResponsiveContainer>
				<ResponsiveContainer width='100%' height={55} debounce={50}>
					<BarChart
						layout='vertical'
						data={[formatData(props.milestoneTasks, 'Milestone Tasks')]}
						margin={{
							top: 10,
							right: 0,
							left: 0,
							bottom: 0,
						}}>
						<XAxis hide axisLine={false} type='number' domain={[0, maxMilestoneTasksValue]} />
						<YAxis type='category' dataKey='name' hide />
						<Tooltip />

						{STATUS_KEYS.map((key) => (
							<Bar key={key} dataKey={key} fill={getColor(key)} stackId='1' barSize={25} radius={getRadius(props.milestoneTasks, key)}>
								{key == 'Complete' && <LabelList dataKey='name' position='top' content={renderCustomYAxisLabel} />}
								<LabelList dataKey={key} content={renderCustomLabel} />
							</Bar>
						))}
					</BarChart>
				</ResponsiveContainer>
			</Card>
		</>
	);
};

export default TasksAndMilestone;
